<template>
  <HKLModal
    ref="modal"
    size="large"
    :closable="true"
    :destroyOnClose="true"
    @open="openModalHandle"
  >
    <CommentsPreviewBodyRecipients
      v-if="isHKlandUser && shouldShowReceivers"
      :receivers="emailReceivers"
      :subject="letterSubject"
    />
    <GreenOperationPreviewBody
      :comments="previewData.comments"
      :greeting="$t('tenancy.greenOperation.letters.toTenants', [companyName])"
    >
      <div
        v-if="isTenant"
        slot="recipientsSlot"
      >
        <div>
          <span class="small-mgn-right">{{ $t('to') }}:</span>
          <span>{{ firstSendReceivers.to }}</span>
        </div>
        <div>
          <span class="small-mgn-right">{{ $t('cc') }}:</span>
          <span>{{ firstSendReceivers.cc }}</span>
        </div>
      </div>
      <div
        slot="additionalInfoSlot"
        class="location section-mgn-btm"
      >
        {{ $t('tenancy.greenOperation.letters.recogScheme') }}
      </div>
    </GreenOperationPreviewBody>
    <TenantSubmissionDocuments
      :files.sync="files"
      :sourceId="(previewData || {}).id || ''"
      :canEdit="false"
    />
    <SendHistory
      v-if="isHKlandUser"
      :histories="sendHistories"
    />
    <ReviewerHistory
      v-if="isHKlandUser"
      :histories="reviewHistories"
      :showPM="false"
    />
    <template slot="footer">
      <div />
    </template>
  </HKLModal>
</template>

<script>
import lodash from 'lodash'
import { computed, getCurrentInstance, ref, unref } from 'vue-demi'
import { emailSeparator } from '@/api/postman/consts'
import { splitEmails } from '@/api/postman/util'
import { getSameBuildingUsers } from '@/api/users/request'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLModal from '@/controls/Modal/HKLModal'
import ReviewerHistory from '@/modules/tenantProjects/ReviewerHistory'
import SendHistory from '@/modules/tenantProjects/SendHistory'
import CommentsPreviewBodyRecipients from '@/modules/tenantProjects/submissions/CommentsPreviewBodyRecipients.vue'
import TenantSubmissionDocuments from '@/modules/tenantProjects/submissions/TenantSubmissionDocuments'
import { concatUserEmails } from '@/modules/tenantProjects/utils'
import { useStoreGetters } from '@/store/index'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { GO_LETTER_HISTORY_ACTION_TYPE } from '../consts'
import { useGreenOperationPerms } from '../GreenOperationLetter.hook'
import GreenOperationPreviewBody from './GreenOperationPreviewBody'

export default {
  components: {
    HKLModal,
    GreenOperationPreviewBody,
    TenantSubmissionDocuments,
    ReviewerHistory,
    SendHistory,
    CommentsPreviewBodyRecipients,
  },

  props: {
    previewData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props) {
    const modals = useRefModal()
    const { proxy } = getCurrentInstance()
    const { isHKlandUser, isTenant, currentUserEmail } = useStoreGetters('user', ['isHKlandUser', 'isTenant', 'currentUserEmail'])
    const { canSend, canPreview } = useGreenOperationPerms()
    const { currentTenancy } = useStoreGetters('ccd', 'currentTenancy')

    const files = ref([])
    const initialEmails = ref({})
    const companyName = computed(() => {
      return lodash.get(unref(currentTenancy), 'company.name', '')
    })
    const buildingName = computed(() => {
      return lodash.get(unref(currentTenancy), 'building.fullName', '')
    })

    const addCurrentUserToCc = (ccList) => {
      if (typeof ccList === 'string') {
        ccList = splitEmails(ccList)
      }
      const defaultCc = !ccList.includes(unref(currentUserEmail)) && unref(canSend) ? [...ccList, unref(currentUserEmail)] : ccList
      return concatUserEmails(defaultCc, emailSeparator)
    }

    const shouldShowReceivers = computed(() => {
      return canPreview.value
    })

    const histories = computed(() => {
      return lodash.orderBy((unref(props.previewData).histories || []), ['createdAt'], ['desc'])
    })
    const reviewHistories = computed(() => {
      const allowedActionTypes = [GO_LETTER_HISTORY_ACTION_TYPE.NEXT_REVIEWER, GO_LETTER_HISTORY_ACTION_TYPE.SEND]
      return unref(histories).filter(item => allowedActionTypes.includes(item.actionType))
    })
    const sendHistories = computed(() => {
      const allowedActionTypes = [GO_LETTER_HISTORY_ACTION_TYPE.RESEND, GO_LETTER_HISTORY_ACTION_TYPE.SEND]
      return unref(histories).filter(item => allowedActionTypes.includes(item.actionType))
    })
    const firstSendReceivers = computed(() => {
      const allowedActionTypes = [GO_LETTER_HISTORY_ACTION_TYPE.SEND]
      const firstSendHistory = unref(histories).find(item => allowedActionTypes.includes(item.actionType)) || {}
      return firstSendHistory.emailReceivers || {}
    })
    const latestReceiverHistory = computed(() => {
      const allowedActionTypes = [GO_LETTER_HISTORY_ACTION_TYPE.RECEIVERS, GO_LETTER_HISTORY_ACTION_TYPE.SEND, GO_LETTER_HISTORY_ACTION_TYPE.RESEND]
      return unref(histories).filter(item => allowedActionTypes.includes(item.actionType))[0] || {}
    })
    const letterSubject = computed(() => {
      return lodash.get(unref(latestReceiverHistory), 'subject') ||
        proxy.$t('tenancy.greenOperation.letters.defaultTitle', [unref(companyName), unref(buildingName)])
    })
    const getInitRecipients = async () => {
      const tenantUsersList = unref(currentTenancy).accounts.filter(account => {
        const item = account.TenancyAccount
        return !!lodash.get(item, 'isAdmin') || (!!lodash.get(item, 'invitedAt') && !!lodash.get(item, 'perms.GreenOperation'))
      })
      const tenantUsers = tenantUsersList.map(recipient => recipient.username)
      const ccWithPerms = await getSameBuildingUsers(
        unref(currentTenancy).building.id,
        unref(currentTenancy).type,
        true,
        PermTypeMapKeys.GREEN_OPERATION_SEND,
      )
      const ccEmailsByPerms = ccWithPerms.map(user => user.email || user.username)
      return { to: concatUserEmails(tenantUsers, emailSeparator), cc: concatUserEmails(ccEmailsByPerms, emailSeparator) }
    }

    const defaultReceivers = computed(() => {
      const latestHistoryReceivers = unref(latestReceiverHistory).emailReceivers || {}
      const initEmails = unref(initialEmails)
      const defaultEmails = Object.keys(latestHistoryReceivers).length ? latestHistoryReceivers : initEmails
      const hasDefaultEmails = Object.keys(defaultEmails).length
      return {
        to: hasDefaultEmails ? defaultEmails.to : '',
        cc: hasDefaultEmails && defaultEmails.cc ? addCurrentUserToCc(defaultEmails.cc) : '',
        bcc: hasDefaultEmails ? defaultEmails.bcc : '',
      }
    })

    return {
      ...modals,
      isHKlandUser,
      isTenant,
      shouldShowReceivers,
      firstSendReceivers,
      files,
      emailReceivers: defaultReceivers,
      reviewHistories,
      sendHistories,
      companyName,
      letterSubject,
      openModalHandle: async () => {
        initialEmails.value = {}
        if (!Object.keys(unref(latestReceiverHistory)).length) {
          initialEmails.value = await getInitRecipients()
        }
      },
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/email_preview';
</style>
